import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  CircularProgress,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAccountBookStore } from '@/store/accountBookStore';
import SectionHeader from '../SectionHeader';
import { getExpensesByUserFromShared } from '@/services';
import { RecordDocument } from '@/services/firestoreService';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  borderRadius: '8px',
  overflow: 'auto',
  maxHeight: 400,
  '&::-webkit-scrollbar': {
    width: '8px',
    height: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'rgba(0,0,0,0.05)',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0,0,0,0.2)',
    borderRadius: '4px',
    '&:hover': {
      background: 'rgba(0,0,0,0.3)',
    }
  },
}));

const StyledTableHead = styled(TableHead)(({ theme }) => ({
  background: 'var(--bg-elev-2)',
  position: 'sticky',
  top: 0,
  zIndex: 1,
  '& .MuiTableCell-head': {
    color: theme.palette.primary.main,
    fontWeight: 700,
    fontSize: '0.95rem',
    textAlign: 'center',
    border: `1px solid ${theme.palette.divider}`,
    padding: '14px 8px',
    backgroundColor: theme.palette.background.paper,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(even)': {
    backgroundColor: 'rgba(0, 0, 0, 0.02)',
  },
  '&:hover': {
    backgroundColor: 'var(--bg-muted)',
    transition: 'background-color 0.2s ease',
  },
  '& .MuiTableCell-body': {
    border: `1px solid ${theme.palette.divider}`,
    padding: '12px 8px',
    fontSize: '0.9rem',
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'left',
  fontWeight: 500,
}));

const NumberCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'right',
  fontWeight: 600,
  fontFamily: 'var(--font-numeric)',
}));

interface MerchantData {
  name: string;
  revenue: number;
  expense: number;
  total: number;
}

const MerchantAnalysisSection: React.FC = () => {
  const theme = useTheme();
  const { dashboardData, userId, currentAccountNumber } = useAccountBookStore();
  const [loading, setLoading] = useState(false);
  const [merchantData, setMerchantData] = useState<MerchantData[]>([]);

  const formatNumber = (num: number): string => {
    return num.toLocaleString('ko-KR');
  };

  // 거래처별 데이터 집계
  useEffect(() => {
    const loadMerchantData = async () => {
      if (!userId || !dashboardData.selectedYear) return;

      setLoading(true);
      try {
        // sharedBooks에서 모든 거래 내역 가져오기 (프리미엄 사용자용)
        const allRecords = await getExpensesByUserFromShared(userId);
        
        // 해당 년도와 가계부 번호로 필터링
        const records = allRecords.filter((record: RecordDocument) => {
          const recordDate = record.createdTime?.toDate();
          if (!recordDate) return false;
          
          const matchesYear = recordDate.getFullYear() === dashboardData.selectedYear;
          const matchesAccount = record.accountNumber === currentAccountNumber;
          
          return matchesYear && matchesAccount;
        });

        const merchantMap = new Map<string, { revenue: number; expense: number }>();

        records.forEach((record: RecordDocument) => {
          const merchant = record.merchantName || '기타';
          if (!merchantMap.has(merchant)) {
            merchantMap.set(merchant, { revenue: 0, expense: 0 });
          }
          const data = merchantMap.get(merchant)!;
          
          // getExpensesByYearAndType에서 가져온 데이터는 이미 타입이 필터링됨
          // revenueRecords는 매출, expenseRecords는 지출
          if (record.incomeType === '매출' || record.incomeType === 'income') {
            data.revenue += record.money || 0;
          } else if (record.incomeType === '지출' || record.incomeType === 'expense') {
            data.expense += record.money || 0;
          }
        });

        // 배열로 변환 및 정렬 (총액 기준)
        const merchantArray: MerchantData[] = Array.from(merchantMap.entries())
          .map(([name, data]) => ({
            name,
            revenue: data.revenue,
            expense: data.expense,
            total: data.revenue + data.expense,
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 20); // 상위 20개만 표시

        setMerchantData(merchantArray);
      } catch (error) {
        console.error('거래처별 데이터 로드 실패:', error);
        setMerchantData([]);
      } finally {
        setLoading(false);
      }
    };

    loadMerchantData();
  }, [userId, dashboardData.selectedYear, currentAccountNumber]);

  // 거래처별 매출 상위 10개
  const topRevenueMerchants = useMemo(() => {
    return merchantData
      .filter(m => m.revenue > 0)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 10);
  }, [merchantData]);

  // 거래처별 지출 상위 10개
  const topExpenseMerchants = useMemo(() => {
    return merchantData
      .filter(m => m.expense > 0)
      .sort((a, b) => b.expense - a.expense)
      .slice(0, 10);
  }, [merchantData]);

  return (
    <Paper 
      elevation={0}
      sx={{ 
        p: { xs: 2, sm: 3 },
        background: 'var(--bg-base)',
        borderRadius: 'var(--r-16)',
        border: '1px solid var(--border-subtle)',
      }}
    >
      <SectionHeader
        eyebrow="거래처"
        title="거래처 분석"
        subtitle={`${dashboardData.selectedYear}년 상위 매출·지출 거래처`}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={2}>
          {/* 거래처별 매출 상위 */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'success.main' }}>
              거래처별 매출 상위 (Top 10)
            </Typography>
            <StyledTableContainer>
              <Table size="small" stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell>순위</StyledTableCell>
                    <StyledTableCell>거래처</StyledTableCell>
                    <NumberCell>매출</NumberCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {topRevenueMerchants.length > 0 ? (
                    topRevenueMerchants.map((merchant, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: 'center' }}>{index + 1}</StyledTableCell>
                        <StyledTableCell>{merchant.name}</StyledTableCell>
                        <NumberCell sx={{ color: 'success.main' }}>
                          {formatNumber(merchant.revenue)}
                        </NumberCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                        매출 데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>

          {/* 거래처별 지출 상위 */}
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1, color: 'error.main' }}>
              거래처별 지출 상위 (Top 10)
            </Typography>
            <StyledTableContainer>
              <Table size="small" stickyHeader>
                <StyledTableHead>
                  <TableRow>
                    <StyledTableCell>순위</StyledTableCell>
                    <StyledTableCell>거래처</StyledTableCell>
                    <NumberCell>지출</NumberCell>
                  </TableRow>
                </StyledTableHead>
                <TableBody>
                  {topExpenseMerchants.length > 0 ? (
                    topExpenseMerchants.map((merchant, index) => (
                      <StyledTableRow key={index}>
                        <StyledTableCell sx={{ textAlign: 'center' }}>{index + 1}</StyledTableCell>
                        <StyledTableCell>{merchant.name}</StyledTableCell>
                        <NumberCell sx={{ color: 'error.main' }}>
                          {formatNumber(merchant.expense)}
                        </NumberCell>
                      </StyledTableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} sx={{ textAlign: 'center', py: 2, color: 'text.secondary' }}>
                        지출 데이터가 없습니다
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StyledTableContainer>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default MerchantAnalysisSection;

